import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Skeleton,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { format } from "date-fns";
import { Message, MessageTypeColor } from "../../../../../types";
import { ReactionAndFavorite } from "../../reaction-and-favorite/reaction-and-favorite";

import { useAppStore } from "../../../../../stores/appStore";
import { motion } from "framer-motion";
import { ImageSwiperModal } from "../image-swiper-modal/image-swiper-modal";
import { useFlashImageCollectionStyles } from "./styles";
import { CustomMarkdown } from "../../../../custom-markdown/custom-markdown";
import { useClampedGalleryIndex } from "../../../../../hooks/useClampedGalleryIndex";
import { useAuthenticatedFileUrl } from "../../../../../hooks/useAuthenticatedFileUrl";

interface FlashImageCollectionProps {
  message: Message;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  // When true, hide ReactionAndFavorite and timestamp (used inside TabbedMessage)
  embedded?: boolean;
}

export const FlashImageCollection: React.FC<FlashImageCollectionProps> = ({
  message,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  embedded = false,
}) => {
  const { isDark } = useAppStore();
  const theme = useTheme();
  const styles = useFlashImageCollectionStyles(isBot, variant, embedded)(theme);

  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<number, boolean>
  >({});
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);

  const title = message.title || "";
  const images = useMemo(
    () => message.image_collection || [],
    [message.image_collection],
  );

  const { safeIndex, setCurrentImageIndex } = useClampedGalleryIndex(
    images.length,
  );
  const currentImageUrl = images[safeIndex]?.url ?? "";
  const { url: resolvedCurrentImageUrl } =
    useAuthenticatedFileUrl(currentImageUrl);

  useEffect(() => {
    if (images.length > 0) {
      setImagesLoaded(new Array(images.length).fill(false));

      const expandedState: Record<number, boolean> = {};
      images.forEach((_, index) => {
        expandedState[index] = true;
      });
      setExpandedDescriptions(expandedState);
    }
  }, [images]);

  if (!images.length) return null;

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleImageClick = () => {
    setIsFullScreenOpen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreenOpen(false);
  };

  const handleImageLoad = (index: number) => {
    const newImagesLoaded = [...imagesLoaded];
    newImagesLoaded[index] = true;
    setImagesLoaded(newImagesLoaded);
  };

  const toggleDescription = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [safeIndex]: !prev[safeIndex],
    }));
  };

  const toggleTitle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsTitleExpanded((prev) => !prev);
  };

  const renderDescription = (description: string) => {
    if (!description) return null;

    const isExpanded = expandedDescriptions[safeIndex] !== false;
    const isLongDescription = description.length > 100;

    if (isLongDescription && !isExpanded) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontFamily: "Red Hat Display", fontWeight: 500 }}
          >
            {description.substring(0, 100)}...
          </Typography>
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              opacity: 0.8,
            }}
          >
            <Box sx={styles.collapseToggle} onClick={toggleDescription}>
              <Typography variant="body2" sx={styles.collapseToggleText}>
                More
              </Typography>
              <ExpandMoreIcon sx={styles.collapseToggleIcon(true)} />
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <CustomMarkdown>{description}</CustomMarkdown>
        {isLongDescription && (
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              opacity: 0.8,
            }}
          >
            <Box sx={styles.collapseToggle} onClick={toggleDescription}>
              <Typography variant="body2" sx={styles.collapseToggleText}>
                Less
              </Typography>
              <ExpandMoreIcon sx={styles.collapseToggleIcon(false)} />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3 },
    },
  };

  const isLongTitle = title.length > 220;

  return (
    <Box sx={styles.container}>
      {title && (
        <Box sx={styles.titleRow}>
          <Box
            sx={
              isLongTitle && !isTitleExpanded
                ? styles.titleCollapsedTypography
                : styles.titleTypography
            }
          >
            <CustomMarkdown skipHtml>{title}</CustomMarkdown>
          </Box>
          {isLongTitle && (
            <Box sx={styles.titleToggleContainer}>
              <Box sx={styles.collapseToggle} onClick={toggleTitle}>
                <Typography variant="body2" sx={styles.collapseToggleText}>
                  {isTitleExpanded ? "Less" : "More"}
                </Typography>
                <ExpandMoreIcon sx={styles.collapseToggleIcon(!isTitleExpanded)} />
              </Box>
            </Box>
          )}
        </Box>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%" } as React.CSSProperties}
      >
        <Box sx={{ position: "relative" }}>
          <motion.div
            key={safeIndex}
            variants={imageVariants}
            initial="hidden"
            animate="visible"
          >
            <Box sx={styles.imageContainer} onClick={handleImageClick}>
              {!imagesLoaded[safeIndex] && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    borderRadius: 3,
                    zIndex: 1,
                    bgcolor: isDark
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                  }}
                />
              )}
              <Box
                component="img"
                src={resolvedCurrentImageUrl || currentImageUrl}
                alt={images[safeIndex]?.description || "Image"}
                sx={{
                  ...styles.image,
                  visibility: imagesLoaded[safeIndex] ? "visible" : "hidden",
                }}
                onLoad={() => handleImageLoad(safeIndex)}
              />
              <Box sx={styles.imageCounter}>
                {safeIndex + 1} / {images.length}
              </Box>
            </Box>
          </motion.div>

          {images.length > 1 && (
            <>
              <IconButton
                sx={styles.navigationButton}
                className="prev"
                onClick={handlePrevImage}
                size="small"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                sx={styles.navigationButton}
                className="next"
                onClick={handleNextImage}
                size="small"
              >
                <NavigateNextIcon />
              </IconButton>
            </>
          )}
        </Box>

        <Box sx={{ mt: 1 }}>
          {renderDescription(images[safeIndex]?.description ?? "")}
        </Box>

        {!embedded && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <ReactionAndFavorite
              currentReaction={currentReaction}
              markedIds={markedIds}
              isSaved={isSaved}
              isTags={isTags}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              handleRemoveTag={handleRemoveTag}
              reactionBg={styles.container.background}
            />
            <Typography variant="caption" sx={styles.timestamp}>
              <span>{format(new Date(message.date), "dd/MM/yy")}</span>
              <span style={{ margin: "0 5px" }} />
              <span>{format(new Date(message.date), "HH:mm")}</span>
            </Typography>
          </Box>
        )}
      </motion.div>

      <ImageSwiperModal
        open={isFullScreenOpen}
        onClose={handleCloseFullScreen}
        images={images}
        initialIndex={safeIndex}
      />
    </Box>
  );
};
